import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { toast } from "sonner";
import { GoogleLogo, Eye, EyeSlash } from "@phosphor-icons/react";
import PageMeta from "@/components/PageMeta";

const NUM_WORDS = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];

export default function Register() {
  const { register } = useAuth();
  const { limits } = useSiteFlags();
  const FREE = limits?.free_recipe_limit ?? 10;
  const FREE_WORD = NUM_WORDS[FREE] || String(FREE);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  // Auto-fill referral code from ?ref=CODE query param. Uppercased so the
  // backend's case-insensitive lookup is a no-op. Persisted through the
  // sign-up so the referrer gets credited when this user later adds a recipe.
  const initialRef = (params.get("ref") || "").toUpperCase().trim();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    referral_code: initialRef,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(
        form.email,
        form.password,
        form.name,
        form.referral_code ? form.referral_code.toUpperCase().trim() : null,
      );
      toast.success("Welcome aboard!");
      nav(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  /* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
  const googleLogin = () => {
    // Persist the referral code across the Google OAuth round-trip via
    // sessionStorage — AuthCallback picks it back up and forwards it to
    // /api/auth/emergent/session so Google signups get credited too.
    if (form.referral_code) {
      try { sessionStorage.setItem("rf_pending_ref", form.referral_code.toUpperCase().trim()); } catch { /* ignore */ }
    }
    // Also stash `next` so AuthCallback can resume post-signup hand-offs
    // like /import?job=X&auto_commit=1 across the Google OAuth trip.
    if (next && next !== "/dashboard") {
      try { sessionStorage.setItem("rf_pending_next", next); } catch { /* ignore */ }
    }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center bg-paper">
      <PageMeta title="Create your free account" description="Start saving family recipes free — no credit card required." noindex />
      <div className="max-w-md mx-auto w-full px-6 py-16">
        <p className="overline mb-3">Start your cookbook</p>
        <h1 data-testid="register-hero" className="font-display text-4xl md:text-5xl font-black text-rf-ink mb-8 leading-tight">{FREE_WORD} free<br />recipes on us.</h1>

        <button onClick={googleLogin} data-testid="google-register-btn" className="btn-outline-ink w-full mb-2">
          <GoogleLogo size={20} weight="duotone" /> Continue with Google
        </button>
        <p className="text-xs text-rf-ink-soft text-center mb-5 leading-relaxed" data-testid="google-consent-note">
          Google may say <span className="font-bold">You&apos;ll see a one-time <span className="font-bold">Emergent</span> consent screen — that&apos;s just our sign-in partner. Your data stays with Recipe Filer only.ldquo;to continue to emergentagent.comYou&apos;ll see a one-time <span className="font-bold">Emergent</span> consent screen — that&apos;s just our sign-in partner. Your data stays with Recipe Filer only.rdquo;</span> — thatYou&apos;ll see a one-time <span className="font-bold">Emergent</span> consent screen — that&apos;s just our sign-in partner. Your data stays with Recipe Filer only.apos;s our secure sign-in partner. Pick your account and youYou&apos;ll see a one-time <span className="font-bold">Emergent</span> consent screen — that&apos;s just our sign-in partner. Your data stays with Recipe Filer only.apos;ll come straight back to RecipeFiler.com. Your recipes stay with Recipe Filer only.
        </p>
        <div className="flex items-center gap-3 my-6 text-xs text-rf-ink-soft uppercase tracking-widest">
          <div className="flex-1 h-px bg-rf-ink/10" /> or <div className="flex-1 h-px bg-rf-ink/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline">Your name</label>
            <input data-testid="register-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-rf mt-1" />
          </div>
          <div>
            <label className="overline">Email</label>
            <input data-testid="register-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-rf mt-1" />
          </div>
          <div>
            <label className="overline">Password</label>
            <div className="relative mt-1">
              <input
                data-testid="register-password"
                required
                type={showPassword ? "text" : "password"}
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="input-rf pr-11 w-full"
              />
              <button
                type="button"
                data-testid="register-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-rf-ink-soft hover:text-rf-orange"
              >
                {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
              </button>
            </div>
            <p className="text-[11px] text-rf-ink-soft mt-1">Tap the eye to check what you&apos;ve typed. Minimum 6 characters.</p>
          </div>

          {/* Referral code — optional. Auto-filled from ?ref=CODE. Hidden
              behind an accordion so it doesn't clutter the flow for people
              who arrived without a link. */}
          <details open={!!initialRef} className="rounded-2xl border border-rf-ink/10 bg-white/60 p-3">
            <summary data-testid="register-refcode-toggle" className="cursor-pointer text-sm font-bold text-rf-ink flex items-center justify-between">
              <span>Have a referral code?</span>
              <span className="text-xs text-rf-ink-soft font-normal">Optional</span>
            </summary>
            <input
              data-testid="register-referral-code"
              value={form.referral_code}
              onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
              placeholder="e.g. HDP7R4"
              maxLength={12}
              className="input-rf mt-3 uppercase tracking-widest"
            />
            <p className="text-[11px] text-rf-ink-soft mt-1">
              A friend gave you a code? Add it here so they get credit — everyone wins.
            </p>
          </details>

          <button data-testid="register-submit" disabled={loading} className="btn-amber w-full">{loading ? "Creating account…" : "Create account"}</button>
        </form>
        <p className="mt-4 text-xs text-rf-ink-soft text-center" data-testid="register-legal-note">
          By creating an account you agree to our <Link to="/terms" className="underline font-bold">Terms</Link> and <Link to="/privacy" className="underline font-bold">Privacy Policy</Link>.
        </p>

        <p className="mt-8 text-sm text-rf-ink-soft text-center">
          Already a member? <Link
            to={next !== "/dashboard" ? `/login?next=${encodeURIComponent(next)}` : "/login"}
            className="font-bold text-rf-orange">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
